import { shallowReactive, computed } from 'vue';

/**
 * Composable for tracking per-entry processing and error state during bulk operations.
 *
 * Uses `shallowReactive(Map)` for O(1) lookups with minimal reactivity overhead—
 * only top-level Map identity triggers updates, not deep entry mutations.
 * A computed plain-object mirror (`stateMap`) bridges the Map into Vue's template
 * reactivity so `v-if` / `:class` bindings re-evaluate correctly.
 *
 * @example
 * const { stateMap, setProcessing, handleResult, clear, getRowClass } =
 *   useEntryProcessState();
 *
 * // Before API call
 * setProcessing(entryIds);
 *
 * // After API response
 * const hasErrors = handleResult(entryIds, result.failures);
 *
 * // In template — pass stateMap to child
 * <EntryTable :entry-process-state="stateMap" />
 *
 * // In v-data-table :row-props
 * const rowClass = getRowClass(item.id);
 */
export function useEntryProcessState() {
  /** @type {Map<string, {processing: boolean, error: string|null}>} */
  const _state = shallowReactive(new Map());

  /**
   * Reactive plain-object snapshot of the Map.
   * Template bindings should read from this, not the Map directly.
   */
  const stateMap = computed(() => {
    const map = {};
    _state.forEach((val, id) => {
      map[id] = val;
    });
    return map;
  });

  // ─── Mutations ───────────────────────────────────────────

  /**
   * Set state for a single entry.
   * @param {string} entryId
   * @param {{processing: boolean, error: string|null}} state
   */
  const setState = (entryId, state) => {
    _state.set(entryId, state);
  };

  /**
   * Mark multiple entries as "processing" (spinner visible).
   * @param {string[]} entryIds
   */
  const setProcessing = entryIds => {
    entryIds.forEach(id => setState(id, {
      processing: true,
      error: null
    }));
  };

  /** Remove all entries from the state map (e.g. after full success). */
  const clear = () => {
    _state.clear();
  };

  // ─── Result Handling ─────────────────────────────────────

  /**
   * Process an API bulk-operation response.
   *
   * - If `errors` is empty → clears all state (full success).
   * - If `errors` has items → marks failed entries with error messages,
   *   marks remaining entries as succeeded (no error).
   *
   * @param {string[]} entryIds  All IDs that were submitted
   * @param {Array}    errors    Failure list from the API (may be empty / undefined)
   * @param {string}   [itemIdKey='entryId']  Property holding the entry ID in each error
   * @param {string}   [reasonKey='error']    Property holding the reason string
   * @returns {boolean} `true` when at least one entry failed (partial failure)
   */
  const handleResult = (entryIds, errors = [], itemIdKey = 'entryId', reasonKey = 'error') => {
    const hasErrors = errors?.length > 0;
    if (!hasErrors) {
      clear();
      return false;
    }
    const failedIds = new Set();
    errors.forEach(e => {
      const id = e[itemIdKey];
      failedIds.add(id);
      setState(id, {
        processing: false,
        error: e[reasonKey] || 'Operation failed'
      });
    });

    // Mark successful entries (removes their spinner)
    entryIds.forEach(id => {
      if (!failedIds.has(id)) {
        setState(id, {
          processing: false,
          error: null
        });
      }
    });
    return true;
  };

  /**
   * Mark every entry in `entryIds` as failed with the same message.
   * Useful when the entire API call throws (network error, 500, etc.).
   * @param {string[]} entryIds
   * @param {string}   [message='Operation failed']
   */
  const setAllFailed = (entryIds, message = 'Operation failed') => {
    entryIds.forEach(id => setState(id, {
      processing: false,
      error: message
    }));
  };

  // ─── Accessors ───────────────────────────────────────────

  /**
   * Get the process state for a single entry (safe — returns default if absent).
   * @param {string} entryId
   * @returns {{processing: boolean, error: string|null}}
   */
  const getState = entryId => stateMap.value[entryId] || {
    processing: false,
    error: null
  };

  /**
   * Returns a Tailwind class string for dimming rows that have errors.
   * Intended used in v-data-table `:row-props`.
   * @param {string} entryId
   * @returns {string}
   */
  const getRowClass = entryId => stateMap.value[entryId]?.error ? 'bg-gray-100 opacity-60' : '';

  /**
   * Whether any entries currently have an error state.
   * Useful for conditional UI (e.g. "Clear errors" button).
   */
  const hasErrors = computed(() => Array.from(_state.values()).some(s => s.error));
  return {
    /** Computed plain-object mirror of the internal Map — pass to child props */
    stateMap,
    /** Whether any entry currently has an error */
    hasErrors,
    /** Set arbitrary state for one entry */
    setState,
    /** Mark entries as processing (spinner) */
    setProcessing,
    /** Clear all process state */
    clear,
    /** Process bulk API result — returns true if partial failure */
    handleResult,
    /** Mark all entries as failed with a uniform message */
    setAllFailed,
    /** Safe getter for one entry's state */
    getState,
    /** Row class string for error dimming */
    getRowClass
  };
}